"use client";

import { useEffect, useState } from "react";

import type { SoundEffectExploreItem } from "@ui/components/sound-effects/SoundEffectsExploreList";
import type { GeneratedSoundItem } from "@ui/components/sound-effects/SoundEffectsGenerationWidget";
import type { DownloadableSoundItem } from "./hooks/useSfxDownload";

import { SoundEffectsAudioPlayerBar } from "@ui/components/sound-effects/SoundEffectsAudioPlayerBar";
import { SoundEffectsGenerationWidget } from "@ui/components/sound-effects/SoundEffectsGenerationWidget";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import clsx from "clsx";

interface SfxStickyBottomWidgetProps {
  activeItem: SoundEffectExploreItem | null;
  generationExpanded: boolean;
  onExpandChange: (expanded: boolean) => void;
  generationPrompt: string;
  generateItems: (prompt: string, loop: boolean) => Promise<GeneratedSoundItem[]>;
  onDownload: (item: DownloadableSoundItem) => void;
  onEditPrompt: (item: SoundEffectExploreItem) => void;
  onGenerateNew: () => void;
  signUpHref: string;
  bumpKey: number;
  hideGenerationWidget?: boolean;
}

export function SfxStickyBottomWidget({
  activeItem,
  generationExpanded,
  onExpandChange,
  generationPrompt,
  generateItems,
  onDownload,
  onEditPrompt,
  onGenerateNew,
  signUpHref,
  bumpKey,
  hideGenerationWidget,
}: SfxStickyBottomWidgetProps) {
  const [isPopping, setIsPopping] = useState(false);

  useEffect(() => {
    if (bumpKey === 0) return;
    setIsPopping(true);
    const id = setTimeout(() => setIsPopping(false), 400);
    return () => clearTimeout(id);
  }, [bumpKey]);

  const content = activeItem ? (
    <SoundEffectsAudioPlayerBar
      title={activeItem.title}
      onEditPrompt={() => onEditPrompt(activeItem)}
      onDownload={() => onDownload(activeItem)}
    />
  ) : !hideGenerationWidget ? (
    <SoundEffectsGenerationWidget
      isExpanded={generationExpanded}
      onExpandChange={onExpandChange}
      initialPrompt={generationPrompt}
      generateItems={generateItems}
      onDownload={item => onDownload(item)}
      onPlay={(_item, index) => {
        captureEvent(EventName.mw_sfx_generation_played, {
          generationIndex: String(index),
          source: "programmatic",
        });
      }}
      onGenerateNew={onGenerateNew}
      signUpHref={signUpHref}
      focusKey={bumpKey}
    />
  ) : null;

  if (!content) return null;

  return (
    <div className='tw-fixed tw-bottom-5 tw-left-4 tw-right-4 tw-z-50 tw-flex tw-justify-center lg:tw-left-1/2 lg:tw-right-auto lg:tw-w-auto lg:-tw-translate-x-1/2'>
      <div className={clsx(isPopping && "tw-animate-[pop_0.4s_ease]")}>{content}</div>
    </div>
  );
}
